import { CORRELATION_ID_COOKIE_NAME } from '@/constants'
import { isValidCorrelationId } from '@/utils/correlationId'
import { getInitialExperimentAttributes } from '@/utils/newRelic/getInitialExperimentAttributes'
import { getSearchQueryAttributes } from '@/utils/newRelic/getSearchQueryAttributes'

const RECOVERABLE_HYDRATION_ERROR_PATTERNS = [
  /Minified React error #(418|423|425)\b/,
  /Hydration failed because/,
  /Text content does not match server-rendered HTML/,
  /There was an error while hydrating/,
]

interface NewRelicBrowserReporter {
  noticeError?: (
    error: Error | string,
    customAttributes?: Record<string, number | string>,
  ) => void
}

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return undefined
}

export const isRecoverableHydrationError = (error: unknown): boolean => {
  const message = getErrorMessage(error)
  return (
    message !== undefined &&
    RECOVERABLE_HYDRATION_ERROR_PATTERNS.some((pattern) =>
      pattern.test(message),
    )
  )
}

const getCorrelationId = (): string | undefined => {
  try {
    const correlationId = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(`${CORRELATION_ID_COOKIE_NAME}=`))
      ?.slice(CORRELATION_ID_COOKIE_NAME.length + 1)

    return isValidCorrelationId(correlationId) ? correlationId : undefined
  } catch {
    return undefined
  }
}

const reportRecoverableHydrationWarning = (error: unknown): boolean => {
  const message = getErrorMessage(error)
  const browserNewRelic = globalThis.newrelic as
    | NewRelicBrowserReporter
    | undefined

  if (message === undefined || browserNewRelic === undefined) return false

  const correlationId = getCorrelationId()
  const customAttributes = {
    reactRecoverableError: 'true',
    reportingSource: 'react-on-recoverable-error',
    severity: 'warning',
    ...(correlationId && { correlationId }),
    ...getSearchQueryAttributes(globalThis.location.search),
    ...getInitialExperimentAttributes(),
  }

  try {
    if (browserNewRelic.noticeError) {
      browserNewRelic.noticeError(
        error instanceof Error ? error : message,
        customAttributes,
      )
      return true
    }
  } catch {
    return false
  }

  return false
}

export const installRecoverableHydrationWarningReporter = (): (() => void) => {
  const originalReportError = globalThis.reportError

  globalThis.reportError = (error: unknown): void => {
    if (
      isRecoverableHydrationError(error) &&
      reportRecoverableHydrationWarning(error)
    ) {
      return
    }

    if (originalReportError) {
      originalReportError.call(globalThis, error)
    } else {
      globalThis.console.error(error)
    }
  }

  return () => {
    if (originalReportError) {
      globalThis.reportError = originalReportError
    } else {
      Reflect.deleteProperty(globalThis, 'reportError')
    }
  }
}
